<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading || isTelematicsLoading"></p-progressBar>

<app-page-shell>

    <app-page-hero
        eyebrow="Fleet Control Tower"
        title="Fleet Operations Dashboard"
        subtitle="Executive and dispatch view for fleet availability, driver coverage, mobilization load, maintenance risk, telematics, and operating cost.">
        <div actions>
            <p-button label="Quick onboard" icon="pi pi-sparkles" severity="secondary" [outlined]="true" (click)="quickFleetOnboardingVisible = true"></p-button>
            <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="loadDashboard()"></p-button>
            <p-button label="Vehicles" icon="pi pi-truck" severity="secondary" [outlined]="true" routerLink="/fleet/vehicles"></p-button>
            <p-button label="Drivers" icon="pi pi-users" severity="secondary" [outlined]="true" routerLink="/fleet/drivers"></p-button>
            <p-button label="Maintenance" icon="pi pi-wrench" routerLink="/fleet/maintenance"></p-button>
        </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="fleetMetrics()"></app-page-metric-strip>

    <div class="fleet-dashboard">
        <section class="availability-command-panel" aria-label="Fleet availability">
            <div class="availability-command-head">
                <div>
                    <span>Fleet Availability</span>
                    <h2>Drivers and trucks ready for dispatch</h2>
                    <p>Live availability, busy resources, next free time, and FIFO recommendations for the next 7 days.</p>
                </div>
                <div class="availability-command-actions">
                    <p-tag [value]="(availability?.fifoPairs?.length || 0) + ' FIFO pairs'" severity="info"></p-tag>
                    <p-button label="Open Availability Cockpit" icon="pi pi-arrow-up-right" (onClick)="openAvailabilityCockpit()"></p-button>
                </div>
            </div>

            <div class="availability-command-grid">
                <article class="availability-kpi" *ngFor="let metric of availabilityMetrics()" [class.success]="metric.tone === 'success'" [class.warn]="metric.tone === 'warn'" [class.info]="metric.tone === 'info'" [class.danger]="metric.tone === 'danger'">
                    <span><i [class]="metric.icon"></i></span>
                    <div>
                        <small>{{ metric.label }}</small>
                        <strong>{{ metric.value }}</strong>
                        <em>{{ metric.detail }}</em>
                    </div>
                </article>
            </div>

            <div class="availability-command-body">
                <div class="availability-focus">
                    <div class="availability-focus-head">
                        <strong>Busy / blocked now</strong>
                        <span>{{ priorityAvailabilityRows().length }} resources</span>
                    </div>
                    <div class="availability-row-list" *ngIf="priorityAvailabilityRows().length; else noAvailabilityRows">
                        <button type="button" *ngFor="let row of priorityAvailabilityRows()" (click)="openAvailabilityRow(row)">
                            <span class="resource-mark"><i [class]="row.resourceType === 'DRIVER' ? 'pi pi-user' : 'pi pi-truck'"></i></span>
                            <span class="availability-row-main">
                                <strong>{{ row.displayName || '-' }}</strong>
                                <small>{{ availabilityWorkLabel(row) }}</small>
                            </span>
                            <span class="availability-row-meta">
                                <p-tag [value]="availabilityStatusLabel(row.availabilityStatus)" [styleClass]="availabilityStatusClass(row.availabilityStatus)"></p-tag>
                                <small>{{ availabilityLocationLabel(row) }}</small>
                            </span>
                            <span class="availability-row-next">
                                <small>Next free</small>
                                <strong>{{ availabilityNextFreeLabel(row) }}</strong>
                            </span>
                        </button>
                    </div>
                    <ng-template #noAvailabilityRows>
                        <div class="availability-empty">No busy or blocked drivers/trucks in the selected availability window.</div>
                    </ng-template>
                </div>

                <div class="fifo-focus">
                    <div class="availability-focus-head">
                        <strong>FIFO recommendations</strong>
                        <span>{{ availability?.fifoPairs?.length || 0 }} pairs</span>
                    </div>
                    <div class="fifo-mini-list" *ngIf="availability?.fifoPairs?.length; else noFifoRows">
                        <div *ngFor="let pair of (availability?.fifoPairs || []).slice(0, 4)">
                            <b>#{{ pair.rank }}</b>
                            <span>
                                <strong>{{ pair.driverName || 'Driver name unavailable' }}</strong>
                                <small>{{ pair.vehicleLabel || 'Vehicle plate unavailable' }} | {{ pair.vehicleTypeName || 'Any type' }}</small>
                            </span>
                            <em>{{ pair.availableAt ? dateTimeLabel(pair.availableAt) : 'Available now' }}</em>
                        </div>
                    </div>
                    <ng-template #noFifoRows>
                        <div class="availability-empty">No FIFO pair can be recommended right now.</div>
                    </ng-template>
                </div>
            </div>
        </section>

        <section class="scorecard-grid" aria-label="Fleet scorecards">
            <div class="fleet-scorecard" *ngFor="let card of scorecards()" [class.success]="card.tone === 'success'" [class.warn]="card.tone === 'warn'" [class.danger]="card.tone === 'danger'" [class.info]="card.tone === 'info'">
                <div class="scorecard-head">
                    <span class="scorecard-icon"><i [class]="card.icon"></i></span>
                    <span>{{ card.label }}</span>
                </div>
                <strong>{{ card.value }}</strong>
                <div class="scorecard-detail">{{ card.detail }}</div>
                <div class="scorebar">
                    <span [style.width.%]="card.progress"></span>
                </div>
            </div>
        </section>

        <div class="map-readiness-grid">
            <app-page-data-panel
                title="Fleet GPS Tracking"
                subtitle="Live vehicle positions, GPS source selection, route replay, coordinates, events, and derived alerts."
                [resultText]="onlineVehicleCount() + ' online'">
                <div class="fleet-tracking-toolbar">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-database"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-select inputId="fleetGpsSource" [options]="gpsSourceOptions()" [(ngModel)]="selectedGpsSourceKey" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" styleClass="w-full" (onChange)="onGpsSourceChange()"></p-select>
                            <label for="fleetGpsSource">GPS source</label>
                        </p-iftaLabel>
                    </p-inputGroup>

                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-datePicker inputId="fleetGpsRange" [(ngModel)]="gpsHistoryRange" selectionMode="range" [showTime]="true" hourFormat="24" appendTo="body" styleClass="w-full" (onSelect)="onGpsHistoryRangeChange()"></p-datePicker>
                            <label for="fleetGpsRange">History range</label>
                        </p-iftaLabel>
                    </p-inputGroup>

                    <div class="fleet-tracking-actions">
                        <p-tag [value]="gpsPollingEnabled ? 'Polling' : 'Paused'" [severity]="gpsPollingEnabled ? 'success' : 'warning'"></p-tag>
                        <p-button icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" [loading]="isTelematicsLoading" (click)="loadTelematics()"></p-button>
                        <p-button [icon]="gpsPollingEnabled ? 'pi pi-pause' : 'pi pi-play'" [label]="gpsPollingEnabled ? 'Pause' : 'Resume'" [outlined]="!gpsPollingEnabled" (click)="toggleGpsPolling()"></p-button>
                    </div>
                </div>

                <div class="fleet-tracking-grid">
                    <div class="fleet-map">
                        <div class="fleet-map-status">
                            <span><i class="pi pi-truck"></i>{{ vehicleList.length }} vehicles</span>
                            <span><i class="pi pi-database"></i>{{ gpsSourceName(selectedGpsSourceKey) }}</span>
                            <span><i class="pi pi-map-marker"></i>{{ gpsHistory.length }} selected trail points</span>
                        </div>
                        <google-map #map id="map-canvas" height="100%" width="100%" [zoom]="zoom" [center]="center" [options]="options">
                            <map-polyline [path]="gpsRoutePath()" [options]="{ strokeColor: '#0f766e', strokeOpacity: 0.92, strokeWeight: 5 }"></map-polyline>
                            <map-marker
                                *ngFor="let vehicle of vehicleList"
                                [position]="animatedVehicleMarkerPosition(vehicle)"
                                [title]="vehicle.plateNumber || ''"
                                [options]="vehicleMarkerOptions(vehicle, selectedTrackingVehicle?.vehicleId === vehicle.vehicleId)"
                                (mapClick)="openInfo(vehicle)">
                                <map-info-window>
                                    <div class="map-popover">
                                        <strong>{{ vehicle.plateNumber || '-' }}</strong>
                                        <span>{{ motionLabel(vehicle) }} | {{ (vehicle.speed || 0) | number:'1.0-0' }} km/h</span>
                                    </div>
                                </map-info-window>
                            </map-marker>
                            @if (replayMarkerPosition(); as replayPosition) {
                            <map-marker [position]="replayPosition" [options]="vehicleMarkerOptions(undefined, true, true)" title="Replay vehicle"></map-marker>
                            }
                        </google-map>
                    </div>

                    <aside class="fleet-tracking-side">
                        <div class="tracking-side-head">
                            <div>
                                <span>Selected vehicle</span>
                                <strong>{{ selectedTrackingVehicle?.plateNumber || selectedTrackingVehicle?.registrationNumber || 'No vehicle selected' }}</strong>
                            </div>
                            <p-tag [value]="gpsStatusLabel(selectedTrackingLive?.liveStatus)" [severity]="gpsStatusSeverity(selectedTrackingLive?.liveStatus)"></p-tag>
                        </div>

                        <div class="tracking-kv-grid">
                            <div><span>Source</span><strong>{{ gpsSourceName(selectedTrackingLive?.sourceKey) }}</strong></div>
                            <div><span>Coordinates</span><strong>{{ gpsCoordinateLabel(selectedTrackingLive?.location) }}</strong></div>
                            <div><span>Speed</span><strong>{{ gpsSpeedLabel(selectedTrackingLive?.location) }}</strong></div>
                            <div><span>Recorded</span><strong>{{ gpsDateLabel(selectedTrackingLive?.location?.recordedAt) }}</strong></div>
                        </div>

                        <div class="tracking-vehicle-list">
                            <button type="button" *ngFor="let vehicle of vehicleList.slice(0, 8)" [class.active]="selectedTrackingVehicle?.vehicleId === vehicle.vehicleId" (click)="selectTrackingVehicle(vehicle)">
                                <span>
                                    <strong>{{ vehicle.plateNumber || ('Vehicle ' + vehicle.vehicleId) }}</strong>
                                    <small>{{ vehicle.source }} | {{ motionLabel(vehicle) }}</small>
                                </span>
                                <p-tag [value]="vehicle.gpsStatus" [severity]="statusSeverity(vehicle.gpsStatus)"></p-tag>
                            </button>
                        </div>

                        <div class="replay-card">
                            <div class="replay-card-head">
                                <span>Replay</span>
                                <strong>{{ replayProgress() }}%</strong>
                            </div>
                            <div class="replay-buttons">
                                <p-button [icon]="replayPlaying ? 'pi pi-pause' : 'pi pi-play'" [label]="replayPlaying ? 'Pause' : 'Play'" (click)="replayPlaying ? pauseReplay() : startReplay()" [disabled]="gpsHistory.length < 2"></p-button>
                                <p-button icon="pi pi-undo" label="Reset" severity="secondary" [outlined]="true" (click)="resetReplay()" [disabled]="!gpsHistory.length"></p-button>
                            </div>
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-bolt"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select inputId="fleetReplaySpeed" [options]="replaySpeeds" [(ngModel)]="replaySpeed" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full" (onChange)="onReplaySpeedChange()"></p-select>
                                    <label for="fleetReplaySpeed">Replay speed</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            <input type="range" min="0" [max]="replayMaxIndex()" [value]="replayIndex" (input)="onReplayIndexChange($any($event.target).value)" [disabled]="!gpsHistory.length" />
                            <small>{{ replayIndex + 1 }} / {{ gpsHistory.length || 0 }} - {{ gpsDateLabel(currentReplayPoint?.recordedAt) }}</small>
                            <p-button icon="pi pi-history" label="Open Full History" [outlined]="true" size="small" [disabled]="!selectedTrackingVehicle" (click)="selectedTrackingVehicle && openTrackingHistory(selectedTrackingVehicle)"></p-button>
                        </div>
                    </aside>
                </div>

                <div class="fleet-tracking-tables">
                    <div>
                        <div class="mini-section-title">
                            <strong>Events</strong>
                            <span>{{ gpsEvents.length }} events</span>
                        </div>
                        <p-table [value]="gpsEvents.slice(0, 6)" styleClass="p-datatable-sm fleet-table">
                            <ng-template pTemplate="header">
                                <tr><th>Event</th><th>Time</th><th>Speed</th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-event>
                                <tr>
                                    <td><p-tag [value]="event.eventType" [severity]="gpsEventSeverity(event.eventType)"></p-tag></td>
                                    <td>{{ gpsDateLabel(event.eventTime) }}</td>
                                    <td>{{ gpsSpeedLabel(event) }}</td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr><td colspan="3"><div class="table-empty">No GPS events for the selected vehicle.</div></td></tr>
                            </ng-template>
                        </p-table>
                    </div>

                    <div>
                        <div class="mini-section-title">
                            <strong>Derived Alerts</strong>
                            <span>{{ derivedGpsAlerts().length }} alerts</span>
                        </div>
                        <div class="fleet-derived-alerts">
                            @for (event of derivedGpsAlerts().slice(0, 6); track event.id) {
                            <div>
                                <p-tag [value]="event.eventType" [severity]="gpsEventSeverity(event.eventType)"></p-tag>
                                <span>{{ gpsDateLabel(event.eventTime) }}</span>
                            </div>
                            } @empty {
                            <div class="table-empty">No derived GPS alerts for this vehicle.</div>
                            }
                        </div>
                    </div>
                </div>
            </app-page-data-panel>

            <app-page-data-panel
                title="Operations Readiness"
                subtitle="Exception-oriented dispatch signals used before assigning assets to shipments or mobilizations.">
                <div class="readiness-list">
                    <div class="readiness-item" *ngFor="let item of readinessItems()">
                        <div>
                            <strong>{{ item.label }}</strong>
                            <span>{{ item.detail }}</span>
                        </div>
                        <p-tag [value]="item.value + ''" [severity]="item.severity"></p-tag>
                    </div>
                </div>
            </app-page-data-panel>
        </div>

        <div class="chart-grid four">
            <app-page-data-panel title="Asset State" subtitle="Moving, idle, offline, and unavailable vehicles.">
                <p-chart type="doughnut" [data]="assetStatusChartData" [options]="chartOptions" height="250px"></p-chart>
            </app-page-data-panel>

            <app-page-data-panel title="Ownership Mix" subtitle="Owned fleet, freelance capacity, and rental exposure.">
                <p-chart type="doughnut" [data]="ownershipChartData" [options]="chartOptions" height="250px"></p-chart>
            </app-page-data-panel>

            <app-page-data-panel title="Driver Readiness" subtitle="License, residence, verification, assignment, and mobilization coverage.">
                <p-chart type="radar" [data]="driverReadinessRadarData" [options]="radarChartOptions" height="250px"></p-chart>
            </app-page-data-panel>

            <app-page-data-panel title="Risk Register" subtitle="Operational risk volume across compliance and maintenance drivers.">
                <p-chart type="polarArea" [data]="riskPolarChartData" [options]="chartOptions" height="250px"></p-chart>
            </app-page-data-panel>
        </div>

        <div class="chart-grid three">
            <app-page-data-panel title="Mobilization Capacity" subtitle="Active assignments against dispatchable asset supply for the next 14 days.">
                <p-chart type="line" [data]="mobilizationTrendChartData" [options]="axisChartOptions" height="280px"></p-chart>
            </app-page-data-panel>

            <app-page-data-panel title="Maintenance Cost Mix" subtitle="Thirty-day labor, parts, external, and variance profile.">
                <p-chart type="bar" [data]="maintenanceCostChartData" [options]="axisChartOptions" height="280px"></p-chart>
            </app-page-data-panel>

            <app-page-data-panel title="Vehicle Downtime" subtitle="Top downtime contributors from maintenance analytics.">
                <p-chart type="bar" [data]="maintenanceDowntimeChartData" [options]="horizontalChartOptions" height="280px"></p-chart>
            </app-page-data-panel>
        </div>

        <div class="chart-grid two">
            <app-page-data-panel title="Mobilization Status" subtitle="Assignment lifecycle mix across draft, approval, active, completed, and rejected states.">
                <p-chart type="bar" [data]="mobilizationStatusChartData" [options]="axisChartOptions" height="280px"></p-chart>
            </app-page-data-panel>

            <app-page-data-panel title="Telematics Economics" subtitle="Distance and speed distribution by fleet source.">
                <p-chart type="bubble" [data]="speedDistanceBubbleData" [options]="bubbleChartOptions" height="280px"></p-chart>
            </app-page-data-panel>
        </div>

        <div class="table-grid two">
            <app-page-data-panel
                title="Vehicle Command Board"
                subtitle="Highest-priority assets by document, maintenance, driver, and telematics risk."
                [resultText]="vehicleTableTotalRecords + ' assets'">
                <div class="table-filter-row">
                    <span class="table-filter-search">
                        <i class="pi pi-search"></i>
                        <input pInputText type="text" placeholder="Search plate, driver, status" [(ngModel)]="vehicleTableSearch" (ngModelChange)="applyVehicleTableFilters()">
                    </span>
                    <p-select [options]="sourceFilterOptions" [(ngModel)]="vehicleSourceFilter" optionLabel="label" optionValue="value" placeholder="Source" [showClear]="true" (ngModelChange)="applyVehicleTableFilters()"></p-select>
                    <p-select [options]="vehicleStateFilterOptions" [(ngModel)]="vehicleStateFilter" optionLabel="label" optionValue="value" placeholder="State" [showClear]="true" (ngModelChange)="applyVehicleTableFilters()"></p-select>
                    <p-button icon="pi pi-filter-slash" [text]="true" severity="secondary" (click)="clearVehicleTableFilters()"></p-button>
                </div>
                <p-table [value]="vehicleTableRows" styleClass="p-datatable-sm fleet-table" [tableStyle]="{ 'min-width': '82rem' }"
                    [lazy]="true" [paginator]="true" [rows]="vehicleTableRowsPerPage" [first]="vehicleTableFirst"
                    [totalRecords]="vehicleTableTotalRecords" [showCurrentPageReport]="true"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} assets"
                    (onLazyLoad)="loadVehicleTable($event)" [rowsPerPageOptions]="[10, 20, 50]">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Asset</th>
                            <th>Source</th>
                            <th>Motion</th>
                            <th>Driver</th>
                            <th>Documents</th>
                            <th>Maintenance</th>
                            <th>Telemetry</th>
                            <th></th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td>
                                <button class="table-link" type="button" (click)="openVehicle(row)">{{ row.plateNumber || '-' }}</button>
                                <div class="muted">{{ row.registrationNumber || '-' }}</div>
                            </td>
                            <td><p-tag [value]="row.source" [severity]="sourceSeverity(row.source)"></p-tag></td>
                            <td><p-tag [value]="motionLabel(row)" [severity]="statusSeverity(motionLabel(row))"></p-tag></td>
                            <td>{{ driverName(row.currentDriver) }}</td>
                            <td>
                                <p-tag [value]="vehicleDocumentStatus(row)" [severity]="statusSeverity(vehicleDocumentStatus(row))"></p-tag>
                                <div class="muted">Reg {{ dateLabel(row.registrationExpiry) }} | Ins {{ dateLabel(row.insuranceExpiry) }}</div>
                            </td>
                            <td>
                                <p-tag [value]="row.maintenanceStatus || 'AVAILABLE'" [severity]="statusSeverity(row.maintenanceStatus)"></p-tag>
                                <div class="muted">{{ row.availabilityReason || 'No active block' }}</div>
                            </td>
                            <td>
                                <strong>{{ (row.speed || 0) | number:'1.0-0' }} km/h</strong>
                                <div class="muted">{{ (row.distance || 0) | number:'1.0-2' }} km</div>
                            </td>
                            <td class="action-cell">
                                <p-button icon="pi pi-map-marker" [rounded]="true" [text]="true" severity="secondary" (click)="trackVehicle(row)"></p-button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="8"><div class="table-empty">No fleet assets loaded.</div></td></tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>

            <app-page-data-panel
                title="Active Mobilizations"
                subtitle="Assignments that currently consume or reserve drivers and vehicles."
                [resultText]="activeMobilizations().length + ' open'">
                <p-table [value]="activeMobilizations()" styleClass="p-datatable-sm fleet-table" [tableStyle]="{ 'min-width': '72rem' }">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Mobilization</th>
                            <th>Project / Site</th>
                            <th>Driver</th>
                            <th>Vehicle</th>
                            <th>Dates</th>
                            <th>Status</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td>
                                <button class="table-link" type="button" (click)="openMobilization(row)">{{ row.mobilizationNumber || ('#' + row.mobilizationId) }}</button>
                                <div class="muted">{{ row.roleName || '-' }}</div>
                            </td>
                            <td>
                                <strong>{{ projectLabel(row) }}</strong>
                                <div class="muted">{{ siteLabel(row) }}</div>
                            </td>
                            <td>{{ row.driverName || '-' }}</td>
                            <td>{{ row.vehiclePlateNumber || '-' }}</td>
                            <td>
                                <strong>{{ dateLabel(row.startDate) }}</strong>
                                <div class="muted">to {{ dateLabel(row.endDate) }}</div>
                            </td>
                            <td><p-tag [value]="row.status || '-'" [severity]="statusSeverity(row.status)"></p-tag></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="6"><div class="table-empty">No active or pending mobilizations.</div></td></tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </div>

        <div class="table-grid two">
            <app-page-data-panel
                title="Driver Coverage"
                subtitle="Driver readiness and assignment exposure for fleet operations."
                [resultText]="driverTableTotalRecords + ' drivers'">
                <div class="table-filter-row">
                    <span class="table-filter-search">
                        <i class="pi pi-search"></i>
                        <input pInputText type="text" placeholder="Search driver, phone, license, vehicle" [(ngModel)]="driverTableSearch" (ngModelChange)="applyDriverTableFilters()">
                    </span>
                    <p-select [options]="sourceFilterOptions" [(ngModel)]="driverSourceFilter" optionLabel="label" optionValue="value" placeholder="Source" [showClear]="true" (ngModelChange)="applyDriverTableFilters()"></p-select>
                    <p-select [options]="documentFilterOptions" [(ngModel)]="driverDocumentFilter" optionLabel="label" optionValue="value" placeholder="Documents" [showClear]="true" (ngModelChange)="applyDriverTableFilters()"></p-select>
                    <p-button icon="pi pi-filter-slash" [text]="true" severity="secondary" (click)="clearDriverTableFilters()"></p-button>
                </div>
                <p-table [value]="driverTableRows" styleClass="p-datatable-sm fleet-table" [tableStyle]="{ 'min-width': '70rem' }"
                    [lazy]="true" [paginator]="true" [rows]="driverTableRowsPerPage" [first]="driverTableFirst"
                    [totalRecords]="driverTableTotalRecords" [showCurrentPageReport]="true"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} drivers"
                    (onLazyLoad)="loadDriverTable($event)" [rowsPerPageOptions]="[10, 20, 50]">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Driver</th>
                            <th>Source</th>
                            <th>Phone</th>
                            <th>Vehicle</th>
                            <th>Documents</th>
                            <th>Verification</th>
                            <th></th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td>
                                <button class="table-link" type="button" (click)="openDriver(row)">{{ driverName(row) }}</button>
                                <div class="muted">{{ row.licenseNumber || '-' }}</div>
                            </td>
                            <td><p-tag [value]="row.source" [severity]="sourceSeverity(row.source)"></p-tag></td>
                            <td>{{ row.phoneNumber || '-' }}</td>
                            <td>{{ row.currentVehicle?.plateNumber || '-' }}</td>
                            <td>
                                <p-tag [value]="driverDocumentStatus(row)" [severity]="statusSeverity(driverDocumentStatus(row))"></p-tag>
                                <div class="muted">License {{ dateLabel(row.licenseExpiryDate) }}</div>
                            </td>
                            <td><p-tag [value]="(row.validated || row.isIDVerified) ? 'Verified' : 'Review'" [severity]="(row.validated || row.isIDVerified) ? 'success' : 'warn'"></p-tag></td>
                            <td class="action-cell">
                                <p-button icon="pi pi-user" [rounded]="true" [text]="true" severity="secondary" (click)="openDriver(row)"></p-button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="7"><div class="table-empty">No drivers loaded.</div></td></tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>

            <app-page-data-panel
                title="Maintenance Queue"
                subtitle="Alerts and open work orders that affect availability, assignment risk, and cost."
                [resultText]="maintenanceQueueRows().length + ' items'">
                <p-table [value]="maintenanceQueueRows()" styleClass="p-datatable-sm fleet-table" [tableStyle]="{ 'min-width': '68rem' }">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Type</th>
                            <th>Issue</th>
                            <th>Asset</th>
                            <th>Owner / Vendor</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td><p-tag [value]="row.type" severity="info"></p-tag></td>
                            <td><strong>{{ row.title }}</strong></td>
                            <td>{{ row.asset }}</td>
                            <td>{{ row.owner }}</td>
                            <td><p-tag [value]="row.status" [severity]="row.severity"></p-tag></td>
                            <td class="action-cell">
                                <p-button icon="pi pi-arrow-up-right" [rounded]="true" [text]="true" severity="secondary" (click)="openMaintenance(row)"></p-button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="6"><div class="table-empty">No maintenance alerts or open work orders.</div></td></tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </div>
    </div>
</app-page-shell>

<app-quick-fleet-onboarding-dialog
    [(visible)]="quickFleetOnboardingVisible"
    (completed)="loadDashboard()">
</app-quick-fleet-onboarding-dialog>
